/**
 * Copyright (c) 2026 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

'use client'

import type { ComponentProps, ReactElement, ReactNode } from 'react'
import { useCallback, useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

import { DEFAULT_COMMON_EMOJIS } from './emoji-picker-popup-common-emojis'

/** Matches `emoji-picker-react` `onEmojiClick` payload shape for drop-in wiring. */
export type EmojiPickerPopupEmojiClickData = {
  emoji: string
}

export type EmojiPickerPopupProps = {
  /** Called when an emoji is chosen; same `{ emoji }` shape as `emoji-picker-react`. */
  onEmojiClick: (data: EmojiPickerPopupEmojiClickData) => void
  /**
   * Trigger element passed to `PopoverTrigger` `render` (e.g. `<Button type="button" variant="ghost" size="icon-xs" />`).
   * Optional children can be passed separately as `triggerChildren` to match Base UI composition.
   */
  trigger: ReactElement
  /** Optional nodes rendered as `PopoverTrigger` children (e.g. icon). */
  triggerChildren?: ReactNode
  /** When false, the trigger should not open the popover (set `disabled` on your trigger as well for consistency). */
  disabled?: boolean
  /** Forwarded to `PopoverTrigger`. */
  nativeButton?: boolean
  open?: boolean
  onOpenChange?: (open: boolean) => void
  /** Override the default frequent-emojis list. */
  emojis?: readonly string[]
  /** Screen reader title for the picker surface. */
  pickerTitle?: string
  className?: string
  contentClassName?: string
  showArrow?: boolean
} & Partial<
  Pick<ComponentProps<typeof PopoverContent>, 'align' | 'alignOffset' | 'side' | 'sideOffset' | 'positionerClassName'>
>

export function EmojiPickerPopup({
  onEmojiClick,
  trigger,
  triggerChildren,
  disabled = false,
  nativeButton = false,
  open: openControlled,
  onOpenChange,
  emojis = DEFAULT_COMMON_EMOJIS,
  pickerTitle,
  className,
  contentClassName,
  showArrow = true,
  align = 'start',
  alignOffset,
  side = 'bottom',
  sideOffset = 4,
  positionerClassName,
}: EmojiPickerPopupProps) {
  const { t } = useTranslation()
  const resolvedPickerTitle = pickerTitle ?? t('Emoji picker')
  const titleId = useId()
  const [openUncontrolled, setOpenUncontrolled] = useState(false)
  const isControlled = openControlled !== undefined
  const open = isControlled ? openControlled : openUncontrolled

  const setOpen = useCallback(
    (next: boolean) => {
      if (!isControlled) {
        setOpenUncontrolled(next)
      }
      onOpenChange?.(next)
    },
    [isControlled, onOpenChange]
  )

  const handlePick = useCallback(
    (emoji: string) => {
      onEmojiClick({ emoji })
      setOpen(false)
    },
    [onEmojiClick, setOpen]
  )

  const root = (
    <Popover
      onOpenChange={(nextOpen) => {
        if (!disabled) {
          setOpen(nextOpen)
        }
      }}
      open={disabled ? false : open}
    >
      <PopoverTrigger disabled={disabled} nativeButton={nativeButton} render={trigger}>
        {triggerChildren}
      </PopoverTrigger>

      <PopoverContent
        align={align}
        alignOffset={alignOffset}
        className={cn(
          'max-h-[min(75vh,22.5rem)] w-[min(100vw-2rem,20rem)] overflow-y-auto p-2 shadow-md',
          contentClassName
        )}
        positionerClassName={positionerClassName}
        showArrow={showArrow}
        side={side}
        sideOffset={sideOffset}
      >
        <PopoverTitle className="sr-only" id={titleId}>
          {resolvedPickerTitle}
        </PopoverTitle>

        <div className="grid grid-cols-8 gap-0.5 p-0.5">
          {emojis.map((emoji) => (
            <button
              aria-label={t('Insert {{emoji}}', { emoji })}
              className={cn(
                'flex size-10 shrink-0 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-3xl leading-none outline-none transition-colors',
                'hover:bg-accent focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50'
              )}
              key={emoji}
              onClick={() => handlePick(emoji)}
              type="button"
            >
              <span aria-hidden>{emoji}</span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )

  return className ? <div className={cn(className)}>{root}</div> : root
}
